import React, { useMemo } from 'react';

// ISIN-wise summary for the GSec report.
// Columns: ISIN, Maturity Date, Total Face Value, Weighted Average Price, Weighted Yield.
// Sorted shorter→longer maturity by the API. Per business rule, price/yield are a
// simple arithmetic average across outstanding buy deals for each ISIN.
const summaryColumns = [
  { key: 'isin', label: 'ISIN', align: 'left' },
  { key: 'maturity_date', label: 'Maturity Date', align: 'center' },
  { key: 'total_face_value', label: 'Total Face Value', align: 'right' },
  { key: 'weighted_avg_price', label: 'Weighted Average Price', align: 'right' },
  { key: 'weighted_yield', label: 'Weighted Yield', align: 'right' },
  { key: 'deal_count', label: 'Deals', align: 'right' },
];

const toNum = (v) => {
  const n = Number(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const fmtMoney = (v) =>
  toNum(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const GsecIsinSummaryTable = ({ summary }) => {
  const faceTotal = useMemo(
    () => (summary || []).reduce((sum, row) => sum + toNum(row.total_face_value), 0),
    [summary]
  );

  if (!summary || summary.length === 0) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-soft p-3 mb-4">
      <h3 className="text-base font-semibold text-gray-700 mb-3">ISIN-wise Summary</h3>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              {summaryColumns.map((col) => (
                <th
                  key={col.key}
                  className="px-4 py-3 bg-gray-50 font-semibold text-gray-700 border-b-2 border-gray-200"
                  style={{ textAlign: col.align }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {summary.map((row, idx) => (
              <tr key={row.isin || idx} className="hover:bg-gray-50">
                {summaryColumns.map((col) => (
                  <td
                    key={col.key}
                    className="px-4 py-3 border-b border-gray-100"
                    style={{ textAlign: col.align }}
                  >
                    {row[col.key] !== undefined && row[col.key] !== null ? row[col.key] : ''}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="bg-gray-50 font-semibold">
              {summaryColumns.map((col) => {
                let value = '';
                if (col.key === 'isin') value = 'Total';
                else if (col.key === 'total_face_value') value = fmtMoney(faceTotal);
                return (
                  <td
                    key={col.key}
                    className="px-4 py-3 border-t-2 border-gray-300"
                    style={{ textAlign: col.align }}
                  >
                    {value}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GsecIsinSummaryTable;
